import { api } from './api';

export interface ProductAttributeValueDto {
  id: number;
  productId: number;
  categoryAttributeId: number;
  categoryAttributeName: string;
  categoryAttributeCode?: string;
  dataType: number; // 0 Number, 1 Text, 2 Date
  unit?: string;
  valueNumeric?: number;
  valueText?: string;
  valueDate?: string;
}

export interface ProductDto {
  id: number;
  productCode: string;
  productName: string;
  barcode?: string;
  categoryId: number;
  categoryName: string;
  productBrandId?: number | null;
  productBrandName?: string | null;
  baseUnitId: number;
  baseUnitName: string;
  description?: string;
  isPriceVariant: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  totalStock?: number;
  retailPrice?: number;
  purchasePrice?: number;
  wholesalePrice?: number;
  taxRate?: number;
  shelfCode?: string;
  shelfLocations?: Array<{ shelfCode: string; quantity: number; warehouseName: string }>;
  attributeValues?: ProductAttributeValueDto[];
}

export interface ProductVariantValueDto {
  variantId: number;
  variantName: string;
  variantValueId: number;
  variantValue: string;
}

export interface ProductVariantDto {
  id: number;
  productId: number;
  sku: string;
  price: number; // Perakende Fiyatı
  wholesalePrice?: number; // Toptan Fiyatı
  purchasePrice?: number; // Alış Fiyatı
  stock: number;
  createdAt: string;
  values: ProductVariantValueDto[];
}

export interface ProductImageDto {
  id: number;
  productId: number;
  originalFileName: string;
  originalFilePath: string;
  thumbnailFilePath: string;
  sortOrder: number;
  isPrimary: boolean;
}

export interface PaginationResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AttributeFilterParam {
  categoryAttributeId: number;
  valueMin?: number;
  valueMax?: number;
  value?: string;
  valueDateMin?: string; // ISO date
  valueDateMax?: string;
}

export async function fetchProducts(
  page: number = 1,
  pageSize: number = 20,
  categoryId?: number,
  search?: string,
  channelId?: number,
  productBrandId?: number,
  brandId?: number,
  modelId?: number,
  yearFrom?: number,
  yearTo?: number,
  attributeFilters?: AttributeFilterParam[],
  inStockOnly?: boolean
): Promise<PaginationResponse<ProductDto>> {
  const params: any = { page, pageSize };
  if (categoryId) params.categoryId = categoryId;
  if (search) params.search = search;
  if (channelId !== undefined && channelId !== null) params.channelId = channelId;
  if (productBrandId != null && productBrandId > 0) params.productBrandId = productBrandId;
  if (brandId) params.brandId = brandId;
  if (modelId) params.modelId = modelId;
  if (yearFrom) params.yearFrom = yearFrom;
  if (yearTo) params.yearTo = yearTo;
  if (attributeFilters && attributeFilters.length > 0) {
    params.attributeFilters = JSON.stringify(attributeFilters);
  }
  if (inStockOnly !== undefined) params.inStockOnly = inStockOnly;
  console.log('📤 fetchProducts params:', params);
  const { data } = await api.get<PaginationResponse<ProductDto>>('/products', { params });
  return data;
}

export async function fetchProductVariants(productId: number): Promise<ProductVariantDto[]> {
  const { data } = await api.get<ProductVariantDto[]>(`/productvariants/by-product/${productId}`);
  return data;
}

export async function deleteProduct(id: number): Promise<void> {
  await api.delete(`/products/${id}`);
}

export interface ProductForDocumentVariantDto {
  id: number;
  sku: string;
  price: number;
  wholesalePrice?: number | null;
  purchasePrice?: number | null;
  stock: number;
  variantValues: Array<{ variantName: string; variantValue: string }>;
}

export interface ProductForDocumentDto {
  id: number;
  productCode: string;
  productName: string;
  categoryId?: number;
  productBrandId?: number | null;
  unitSetId: number;
  baseUnitId: number;
  taxRate?: number;
  isPriceVariant?: boolean;
  baseStock: number;
  retailPrice?: number | null;
  wholesalePrice?: number | null;
  purchasePrice?: number | null;
  variants: ProductForDocumentVariantDto[];
  units: Array<{ id: number; name: string; symbol: string; baseRatio: number }>;
}

export interface ProductForDocumentsPagedParams {
  page?: number;
  pageSize?: number;
  search?: string;
  searchType?: 'name' | 'code' | 'barcode';
  categoryId?: number;
  productBrandId?: number;
  inStockOnly?: boolean;
}

export async function fetchProductsForDocumentsPaged(
  params: ProductForDocumentsPagedParams
): Promise<PaginationResponse<ProductForDocumentDto>> {
  const { data } = await api.get<PaginationResponse<ProductForDocumentDto>>('/products/for-documents/paged', {
    params,
  });
  return data;
}

export async function fetchProductImages(productId: number): Promise<ProductImageDto[]> {
  const { data } = await api.get<ProductImageDto[]>(`/productimages/product/${productId}`);
  return data;
}

